import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Grid, Box, Typography, Paper, Chip } from "@mui/material";
import dayjs from "dayjs";
import tz from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import FormResponseTable from "../FormResponse/FormResponseTable";
import MyFormFilterDrawer from "../MyForm/MyFormFilterDrawer";
import NoDataAvailable from "../../../components/NoDataAvailable/NoDataAvailable";
import Loader from "../../../components/Loader/Loader";
import IconButtons from "../../../components/IconButtons/IconButtons";
import UserTableAvatar from "../../../components/UserTableAvatar/UserTableAvatar";

import { calculateFilterCount, font_15, getStatus } from "../../../utils/utils";
import { filter_active_icon } from "../../../constants/icons";
import { HelpTexts } from "../../../constants/constants";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BootstrapTooltip from "../../../components/BootstrapTooltip/BootstrapTooltip";

import {
  clearFormResponse,
  fetchMyFeedbacks,
  fetchResponseListingFilterOptionsForAttendee,
} from "../../../store/formResponseSlice";

dayjs.extend(utc);
dayjs.extend(tz);

const { NON_ACTIVE_ATTENDEE } = HelpTexts;

const status = {
  statuses: [
    { id: 0, title: "Active", value: "ACTIVE", variant: "GREEN-TEXT" },
    { id: 1, title: "Inactive", value: "INACTIVE", variant: "RED-TEXT" },
    { id: 3, title: "Completed", value: "COMPLETED", variant: "GREEN-TEXT" },
    { id: 4, title: "Pending", value: "PENDING", variant: "RED-TEXT" },
  ],
  getVariant: (value) => status.statuses.find((it) => it.value === value)?.variant || "",
  getTitle: (value) => status.statuses.find((it) => it.value === value)?.title || "",
};

const getScoreClass = (val) => {
  const score = parseFloat(val);
  if (isNaN(score)) return null;
  if (score >= 0 && score <= 2.5) return "score-chip chip-red";
  if (score > 2.5 && score < 3.8) return "score-chip chip-yellow";
  if (score >= 3.8) return "score-chip chip-green";
  return "score-chip chip-default";
};

const formatDate = (date) => {
  if (!date) return "-";
  const parsedDate = new Date(date);
  if (isNaN(parsedDate.getTime())) return "-";
  return parsedDate.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

function prepareFormOptions(forms) {
  if (!Array.isArray(forms)) return [];
  return forms.map(({ id, label, type, description }) => ({
    id,
    name: label,
    type,
    description: description || "",
  }));
}

const ExpandableList = ({ items }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!Array.isArray(items) || items.length === 0) return "-";

  const initialCount = 2;
  const shouldShowAction = items.length > initialCount;
  const displayItems = isExpanded ? items : items.slice(0, initialCount);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "0.2rem", py: 0.5 }}>
      {displayItems.map((item, idx) => (
        <Typography key={`${item}-${idx}`} variant="font_12">
          {item}
        </Typography>
      ))}

      {shouldShowAction && (
        <Box
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            mt: 0.5
          }}
        >
          <Typography
            variant="font_9_bold"
            color="secondary"
            sx={{ fontWeight: 600, textTransform: "uppercase" }}
          >
            {isExpanded ? "VIEW LESS" : "VIEW MORE"}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const extractUserFields = (user = {}) => ({
  newerId: user.employeeCode || "",
  fullName: user.fullName || "",
  profilePictureUrl: user.profilePictureUrl || "",
  newerEmail: user.email || "",
  userDeleted: user.deleted || false,
  competency: user.competency?.name || "",
  employeeStatus: user.deleted ? "INACTIVE" : "ACTIVE",
});

const extractMentorFields = (mentor = {}) => ({
  mentorFullName: mentor.fullName || "",
  mentorEmail: mentor.email || "",
  mentorProfilePictureUrl: mentor.profilePictureUrl || "",
  mentorDeleted: mentor.deleted || false,
  mentorId: mentor.employeeCode,
});

const extractReportingManagerFields = (reportingManager = {}) => ({
  reportingManagerFullName: reportingManager.fullName || "",
  reportingManagerEmail: reportingManager.email || "",
  reportingManagerProfilePictureUrl: reportingManager.profilePictureUrl || "",
  reportingManagerDeleted: reportingManager.deleted || false,
  reportingManagerId: reportingManager.employeeCode,
});

const extractEvaluatedByFields = (evaluatedby = {}) => ({
  evaluatedby: evaluatedby?.fullName || "",
  evaluatedbyEmail: evaluatedby?.email || "",
  evaluatedbyProfilePictureUrl: evaluatedby?.profilePictureUrl || "",
  evaluatedbyDeleted: evaluatedby?.deleted || false,
  evaluatedbyId: evaluatedby?.employeeCode,
});

const extractUserData = (user = {}) => {
  const mentor = user.mentor || {};
  const reportingManager = user.reportingManager || {};
  return {
    ...extractUserFields(user),
    ...extractMentorFields(mentor),
    ...extractReportingManagerFields(reportingManager),
  };
};

const extractAssessmentData = (item = {}) => ({
  dateOfAssessment: formatDate(item.trigger?.assessmentDate),
  assessmentPeriod: `${formatDate(item.trigger?.periodFrom)} - ${formatDate(
    item.trigger?.periodTo
  )}`,
  assessmentName: item.trigger?.assessmentName,
  bootcamp: item.trigger?.bootcamp.name,
  status: item.status,
});

const buildDynamicColumnsData = (inputs = []) =>
  inputs.reduce((acc, input) => {
    const label = input?.input?.label || "Unknown";
    const type = input?.input?.type;
    const rawValue = input?.value || [];
    
    const isMultiSelect = ["PROJECT_NAME", "PERFORMANCE_ASSESSED_BY"].includes(type);

    acc[label] = {
      value: isMultiSelect ? rawValue : (Array.isArray(rawValue) ? rawValue.join(", ") : rawValue),
      tooltip: input?.input?.description || "",
    };
    return acc;
  }, {});

const mapApiToTableRows = (apiResponse = []) =>
  apiResponse.map((item) => ({
    ...extractUserData(item.filledForUser),
    ...extractAssessmentData(item),
    ...buildDynamicColumnsData(item.inputResponses?.results),
    ...extractEvaluatedByFields(item.filledByUser),
    original: item,
  }));


const createNameEmailColumn = (attendeeStatuses) => ({
  key: "fullName",
  label: "Name",
  render: (value, row) => (
    <UserTableAvatar
      imgAlt={row.fullName}
      imgSrc={row.profilePictureUrl}
      href={row.newerId}
      label={row.fullName}
      subLabel={row.newerEmail}
      deleted={row.userDeleted}
      userId={row.newerId}
      tooltip={
        row.userDeleted
          ? NON_ACTIVE_ATTENDEE(
            getStatus(attendeeStatuses, row.attendeeStatus),
            dayjs(row.statusUpdatedDate)
              .tz(dayjs.tz.guess())
              .format("DD MMM YYYY")
          )
          : null
      }
    />
  ),
});

const createMentorEmailColumn = () => ({
  key: "mentorEmail",
  label: "Mentor",
  render: (value, row) => (
    <UserTableAvatar
      imgAlt={row.mentorFullName}
      imgSrc={row.mentorProfilePictureUrl}
      href={row.mentorId}
      label={row.mentorFullName}
      subLabel={row.mentorEmail}
      deleted={row.mentorDeleted}
      userId={row.mentorId}
    />
  ),
});

const createEvaluatedByEmailColumn = () => ({
  key: "evaluatedBy",
  label: "Evaluated By",
  render: (value, row) => (
    <UserTableAvatar
      imgAlt={row.evaluatedby}
      imgSrc={row.evaluatedbyProfilePictureUrl}
      href={row.evaluatedbyId}
      label={row.evaluatedby}
      subLabel={row.evaluatedbyEmail}
      deleted={row.evaluatedbyDeleted}
      userId={row.evaluatedbyId}
    />
  ),
});

const createReportingManagerColumn = () => ({
  key: "reportingManagerEmail",
  label: "Reporting Manager",
  render: (value, row) => (
    <UserTableAvatar
      imgAlt={row.reportingManagerFullName}
      imgSrc={row.reportingManagerProfilePictureUrl}
      href={row.reportingManagerId}
      label={row.reportingManagerFullName}
      subLabel={row.reportingManagerEmail}
      deleted={row.reportingManagerDeleted}
      userId={row.reportingManagerId}
    />
  ),
});

const createStatusColumn = (key, label) => ({
  key: key,
  label: label,
  minWidth: 150,
  maxWidth: 150,
  render: (value) => (
    <Box className="empoyee-status">
      <Chip
        className="same-size-chip"
        variant={status.getVariant(value)}
        label={status.getTitle(value)}
      />
    </Box>
  ),
});

const buildDynamicColumns = (apiData = []) => {
  const columnMap = new Map();

  apiData.forEach(item => {
    item?.inputResponses?.results?.forEach(input => {
      const label = input?.input?.label;
      
      if (!label || columnMap.has(label)) return;

      const isAverageScore = input?.input?.type === "AVERAGE_SCORE";

      columnMap.set(label, {
        key: label,
        label,
        tooltip: input?.input?.description || "",
        render: (cell) => {
          if (isAverageScore && cell?.value && cell.value !== "-") {
            const className = getScoreClass(cell.value);
            if (className) {
              return (
                <Chip 
                  label={cell.value} 
                  size="small" 
                  className={className}
                />
              );
            }
          }
          return (
            <Box title={cell?.tooltip || ""}>
              {cell?.value || "-"}
            </Box>
          );
        },
      });
    });
  });

  return Array.from(columnMap.values());
};

const MyFeedbacks = () => {
    const dispatch = useDispatch();
    const isInitialized = useRef(false);

    const { list: apiData = [], loading = false, totalResults = 0 } = useSelector((state) => state.formResponse || {});
    const attendeeStatuses = useSelector((state) => state?.utility?.constants?.attendeeStatuses);
    const security = useSelector((state) => state?.security);
    
    const isAttendee = security?.loggedInUser?.permission?.attendee === true;

    const [openFilter, setOpenFilter] = useState(false);
    const [formNameOptions, setFormNameOptions] = useState([]);
    
    const [isFilter, setIsFilter] = useState({
        formName: null
    });

    const [offset, setOffset] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedFormType, setSelectedFormType] = useState("");

    useEffect(() => {
        if (isInitialized.current) return;
        isInitialized.current = true;

        const loadOptions = async () => {
            const resultAction = await dispatch(fetchResponseListingFilterOptionsForAttendee()).unwrap();
            const { form } = resultAction || {};
            const formOptions = prepareFormOptions(form?.results);
            setFormNameOptions(formOptions);

            if (formOptions.length) {
                const defaultFilter = { ...isFilter, formName: formOptions[0] };
                setIsFilter(defaultFilter);
                setSelectedFormType(formOptions[0].type);
                applyFilter(defaultFilter, 0);
            }
        };
        loadOptions();
    }, [dispatch]);

    useEffect(() => {
    return () => {
      dispatch(clearFormResponse());
    };
  }, [dispatch]);

    const applyFilter = useCallback((filter = isFilter, pageOffset = offset) => {
        if (!filter?.formName?.id) return;
        
        dispatch(clearFormResponse());
        dispatch(fetchMyFeedbacks({
            formId: filter.formName.id,
            offset: pageOffset,
        }));
    }, [dispatch]);


    const rows = useMemo(() => {
        const dataToMap = Array.isArray(apiData) ? apiData : apiData?.results || [];
        return mapApiToTableRows(dataToMap);
    }, [apiData]);

    const columns = useMemo(() => {
        const currentType = selectedFormType;
        const staticColumns = [
            createNameEmailColumn(attendeeStatuses),
            createMentorEmailColumn(),
            createReportingManagerColumn(),
            createEvaluatedByEmailColumn(),
            createStatusColumn("status", "Status"),
            { key: "competency", label: "Competency", minWidth: 120, maxWidth: 120 },
            { key: "bootcamp", label: "Bootcamp" },
        ];

        if (currentType === "CLASSROOM_ASSESSMENT_PERFORMANCE") {
            staticColumns.push(
                { key: "assessmentName", label: "Assessment Name" },
                { key: "dateOfAssessment", label: "Date of Assessment" }
            );
        } else {
            staticColumns.push({
                key: "assessmentPeriod",
                label: "Assessment Period",
            });
        }

        const dynamicColumns = buildDynamicColumns(apiData?.results);
        return [...staticColumns, ...dynamicColumns];
    }, [apiData, selectedFormType, attendeeStatuses]);


    const handleFilterChange = (name, value) => {
        setIsFilter((prev) => ({ ...prev, [name]: value }));
    };

    const handleFilterSubmit = () => {
        setOffset(0);
        setCurrentPage(1);
        setOpenFilter(false);
        if (isFilter?.formName) {
            setSelectedFormType(isFilter.formName.type);
            applyFilter(isFilter, 0);
        }
    };

    const handleFilterRemove = () => {
         const firstForm = formNameOptions[0];
         if(firstForm) {
             const resetFilter = { ...isFilter, formName: firstForm };
             setIsFilter(resetFilter);
             setSelectedFormType(firstForm.type);
             applyFilter(resetFilter, 0);
         }
         setOpenFilter(false);
    };

    const handlePageChange = (page) => {
        const newOffset = page - 1;
        setCurrentPage(page);
        setOffset(newOffset);
        applyFilter(isFilter, newOffset);
    };

    const filterCount = isFilter.formName ? 1 : 0;
    const filterActivated = filterCount > 0;
    const totalPages = Math.ceil(totalResults / 20);

    if (!isAttendee) {
        return <Typography sx={{p:3}}>You do not have permission to view this page.</Typography>;
    }

    return (
        <>
            {loading && <Loader />}
            <Grid container className="main-container">
        <Grid 
          item 
          md={12} 
          className="header-container" 
          sx={{ 
            height: "auto !important",
            minHeight: "60px",
          }}
        >        
          <Box
            display="flex"
            alignItems="flex-start" 
            justifyContent="space-between"
            width="100%"
          >
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Typography variant="font_21_bold">
                {isFilter?.formName?.name || "My Feedbacks"}
              </Typography>

              {isFilter?.formName?.description && (
                <Box display="flex" alignItems="flex-start" gap={0.5} sx={{ mt: 0.2 }}>
                  <InfoOutlinedIcon 
                    sx={{ 
                      fontSize: "1rem", 
                      color: "#de1186", 
                      mt: "3px",
                      flexShrink: 0
                    }} 
                  />
                  <Typography
                    variant="font_12"
                    component="div"
                    sx={{ 
                      color: "#4b4b4b", 
                      fontStyle: "italic",
                      wordBreak: "break-word",
                      "& p": { margin: 0 } 
                    }}
                    dangerouslySetInnerHTML={{
                      __html: isFilter.formName.description,
                    }}
                  />
                </Box>
              )}
            </Box>

            <Box display="flex" alignItems="center" sx={{ alignSelf: "flex-start", mt: 0.5 }}>
              <Box
                className="filterButton"
                sx={{
                  border: filterActivated
                    ? "2px solid #de1186"
                    : "1px solid #de118680",
                }}
              >
                <IconButtons
                  height={font_15}
                  width={font_15}
                  image={filter_active_icon}
                  handleClick={() => setOpenFilter(true)}
                  tooltip="Add Filters"
                  count={filterCount}
                />
              </Box>
            </Box>
          </Box>
        </Grid>

                <Grid item md={12} className="body-container">
                    <Grid container className="table-parent-container">
                        <Paper elevation={0} className="flex-container-column topic-paper-container">
                             {!rows.length ? (
                                <Grid container className="no-data-container">
                                    <NoDataAvailable imgHeight={4} imgWidth={4.5} message="No Feedbacks Available" />
                                </Grid>
                            ) : (
                                <FormResponseTable
                                    title="My Feedbacks"
                                    actions={[]} // NO ACTIONS
                                    columns={columns}
                                    rows={rows}
                                    totalResult={totalResults}
                                    totalPages={totalPages}
                                    currentPage={currentPage}
                                    handlePageChange={handlePageChange}
                                />
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </Grid>

            <MyFormFilterDrawer
                open={openFilter}
                onClose={() => setOpenFilter(false)}
                handleFilterChange={handleFilterChange}
                handleFilterSubmit={handleFilterSubmit}
                handleFilterRemove={handleFilterRemove}
                filterValue={isFilter}
                formNameOptions={formNameOptions}
                hideStatusSelect={true}
            />
        </>
    );
};

export default MyFeedbacks;